import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, MapPin, Sparkles, FileDown, ExternalLink, LogOut, Brain, Share2, ArrowRightLeft, FileText } from "lucide-react";
import { BackToPortfolioButton, usePmAccess } from "@/components/BackToPortfolioButton";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { HealthScoreGauge, BAND_CFG } from "@/components/passport/HealthScoreGauge";
import { TrendChart } from "@/components/passport/TrendChart";
import { ScoreTrendChart } from "@/components/passport/ScoreTrendChart";
import { ShareModal, TransferModal } from "@/components/PropertyShareAndTransfer";
import { useFeature } from "@/lib/useFeature";
import { PristineQuoteCTA } from "@/components/PristineQuoteCTA";
import { PropertyTimeline } from "@/components/PropertyTimeline";
import { ConveyancingCertButton } from "@/components/ConveyancingCertButton";
import { PropertyInfoCard } from "@/components/PropertyInfoCard";
import { CustomerActionButtons } from "@/components/CustomerActionButtons";
import { AdwgComparisonTable } from "@/components/AdwgComparisonTable";
import { LsiIndexCard } from "@/components/LsiIndexCard";
import { Bore360Panel } from "@/components/Bore360Panel";

const PARAMS = ["ph", "conductivity", "tds", "e_coli", "coliforms"];

export default function PassportProperty() {
  const { id } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [reportMeta, setReportMeta] = useState(null);
  const [adwgRows, setAdwgRows] = useState([]);
  const [lsi, setLsi] = useState(null);
  const [lsiHistory, setLsiHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Phase 2B state
  const [showShare, setShowShare] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  // Feature-flag gating — hide internal_only/disabled features from non-staff users
  const sharing = useFeature("property_sharing");
  const transfers = useFeature("property_transfers");
  const conveyancing = useFeature("conveyancing_certificates");
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [certMeta, setCertMeta] = useState({ available: false });

  useEffect(() => {
    api.get(`/passport/property/${id}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
    api.get(`/passport/property/${id}/ai-summary`).then(({ data }) => setAiSummary(data)).catch(() => setAiSummary(null));
    api.get(`/passport/property/${id}/report-meta`).then(({ data }) => setReportMeta(data)).catch(() => setReportMeta(null));
    api.get(`/properties/${id}/timeline`).then(({ data }) => setTimelineEvents(data.events || [])).catch(() => setTimelineEvents([]));
    api.get(`/properties/${id}/certificate-meta`).then(({ data }) => setCertMeta(data)).catch(() => setCertMeta({ available: false }));
    api.get(`/passport/property/${id}/adwg-comparison`).then(({ data }) => { setAdwgRows(data.rows || []); setLsi(data.lsi || null); setLsiHistory(data.lsi_history || []); }).catch(() => { setAdwgRows([]); setLsi(null); setLsiHistory([]); });
  }, [id]);

  const downloadReport = async () => {
    const token = localStorage.getItem("scwt_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/passport/property/${id}/report.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "water-report.pdf";
    a.click();
  };

  // Phase 2L-A113 — open a SPECIFIC test's report from a timeline event.
  // Fetches the per-test COA with the user's auth header (cannot be a
  // plain <a href> because PDF endpoints require Bearer auth) and opens
  // the resulting blob in a new tab.
  const openReportForEvent = async (ev) => {
    const tid = ev?.metadata?.test_id;
    if (!tid) return;
    const token = localStorage.getItem("scwt_token");
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/passport/property/${id}/report.pdf?test_id=${encodeURIComponent(tid)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        toast.error(r.status === 404 ? "Report not available yet" : "Could not open report");
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Could not open report");
    }
  };

  const downloadCertificate = async () => {
    if (!certMeta.available) {
      toast.info("A Property Water History Certificate becomes available after the property's first completed water test.");
      return;
    }
    const token = localStorage.getItem("scwt_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${id}/certificate.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) {
      toast.error("Could not download certificate");
      return;
    }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `water-history-${id.slice(0, 8)}.pdf`;
    a.click();
  };

  const isPm = usePmAccess();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading Passport...</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Property not found. <Link className="ml-2 text-cyan-600 font-semibold" to="/passport">Back →</Link></div>;

  const { property: p, tests, schedules, latest, recommendations, report_facts: reportFacts } = data;
  const bandCfg = latest ? BAND_CFG[latest.band] : null;
  const sched = schedules?.[0];
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  // Deterministic safety override — when the lab found micro-positive results,
  // FORCE the band wording to "Action Required" regardless of what the AI or
  // a stale cached band field says. Single source of truth for safety claims.
  const microStatus = reportFacts?.microbiological_status; // critical | attention | clear | untested
  const overrideToAction = microStatus === "critical" || microStatus === "attention" || reportFacts?.overall_status === "action_required";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <BrandLogo to="/" size="sm" />
          <button onClick={async () => { await logout(); window.location.assign("/"); }} className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Phase 2L-A132C — single back navigation. PMs see the
            BackToPortfolioButton pill ("← Back to Managed Properties");
            customers see the "All properties" text link. We never
            render both so PMs don't see two duplicate back buttons. */}
        {isPm ? (
          <BackToPortfolioButton className="mb-4" />
        ) : (
          <Link to="/passport" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> All properties
          </Link>
        )}

        {/* Phase 2B: Property actions — visibility gated by feature flags */}
        <div className="mt-5 flex flex-wrap gap-2">
          {sharing.isVisible && (
            <button
              data-testid="passport-share-btn"
              onClick={() => setShowShare(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-cyan-500 hover:text-cyan-700 text-sm font-semibold text-slate-700 transition-colors"
            >
              <Share2 className="w-4 h-4" /> Share Passport
            </button>
          )}
          {transfers.isVisible && (
            <button
              data-testid="passport-transfer-btn"
              onClick={() => setShowTransfer(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-amber-500 hover:text-amber-700 text-sm font-semibold text-slate-700 transition-colors"
            >
              <ArrowRightLeft className="w-4 h-4" /> Transfer property
            </button>
          )}
          {conveyancing.isVisible && (
            <>
              <button
                data-testid="passport-certificate-btn"
                onClick={downloadCertificate}
                disabled={!certMeta.available}
                title={certMeta.available ? "Download Property Water History Certificate" : "Becomes available after the first completed water test"}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-cyan-500 hover:text-cyan-700 text-sm font-semibold text-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="w-4 h-4" /> Download Water History
              </button>
              <ConveyancingCertButton propertyId={id} certAvailable={certMeta.available} />
            </>
          )}
        </div>

        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 bg-white rounded-3xl border border-slate-200 p-7 lg:p-9">
            <div className="flex items-center gap-2 text-xs text-cyan-600 font-semibold uppercase tracking-wider">
              <MapPin className="w-3.5 h-3.5" /> {p.property_type}
            </div>
            <h1 data-testid="passport-property-title" className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
              {p.nickname || p.address}
            </h1>
            {p.nickname && <p className="text-sm text-slate-500 mt-1">{p.address}</p>}

            <div className="mt-7 flex flex-wrap gap-2">
              {(p.sources || []).map((s) => (
                <span key={s.id} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium capitalize">
                  {s.type.replace("_", " ")}
                </span>
              ))}
            </div>

            {data.pending_tests && data.pending_tests.length > 0 && (
              <div data-testid="passport-pending-banner" className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm">
                <div className="font-semibold text-amber-900">Your sample{data.pending_tests.length > 1 ? "s are" : " is"} currently being processed.</div>
                <ul className="mt-1 text-amber-800 text-sm space-y-0.5">
                  {data.pending_tests.map((t) => (
                    <li key={t.id}>
                      <span className="font-mono">{t.booking_reference}</span> · {t.test_name} · status: <span className="capitalize">{t.status.replace(/_/g, " ")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Calm note when one or more parameters were intentionally
                Not Tested for this sample. Source-of-truth: report_facts.not_tested. */}
            {latest && reportFacts?.not_tested?.length > 0 && (
              <div data-testid="passport-not-tested-note" className="mt-4 rounded-xl bg-slate-100 border border-slate-200 px-3 py-2 text-[12px] text-slate-700">
                Some parameters ({reportFacts.not_tested.length}) were not tested and have been marked accordingly in the results table.
              </div>
            )}

            {/* Deterministic safety banner — non-negotiable wording sourced from
                report_facts on the backend. Renders above the score gauge so
                a stale band or AI hallucination cannot ever obscure a
                micro-positive lab result. */}
            {latest && reportFacts && overrideToAction && (
              <div data-testid="safety-banner" className={`mt-6 rounded-2xl border-2 p-5 ${microStatus === "critical" ? "bg-rose-50 border-rose-300" : "bg-amber-50 border-amber-300"}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${microStatus === "critical" ? "bg-rose-600" : "bg-amber-600"} text-white font-bold`}>!</div>
                  <div className="flex-1">
                    <div data-testid="safety-banner-label" className={`text-[10px] uppercase tracking-[0.18em] font-bold ${microStatus === "critical" ? "text-rose-700" : "text-amber-700"}`}>
                      {microStatus === "critical" ? "Action Required" : "Attention Required"}
                    </div>
                    <p data-testid="safety-banner-headline" className={`mt-1 text-[15px] leading-relaxed font-semibold ${microStatus === "critical" ? "text-rose-900" : "text-amber-900"}`}>
                      {reportFacts.headline}
                    </p>
                    {(reportFacts.e_coli_detected || reportFacts.coliforms_detected) && (
                      <ul className="mt-2 space-y-0.5 text-sm">
                        {reportFacts.e_coli_detected && (
                          <li data-testid="safety-banner-ecoli" className="text-rose-900"><strong>E. coli:</strong> Positive (detected in this sample)</li>
                        )}
                        {reportFacts.coliforms_detected && (
                          <li data-testid="safety-banner-coliforms" className="text-rose-900"><strong>Coliforms:</strong> Positive (detected in this sample)</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Water Health Score™ — single full-width highlight box.
                A subtle tinted background runs across the entire box so the
                score, status pill and explanation feel like one unit. The
                left accent stripe carries the band colour for at-a-glance
                visual cueing and remains the only "decorated" piece in
                B&W print mode. */}
            {latest && (bandCfg || overrideToAction) && (() => {
              const accent = overrideToAction ? "#dc2626" : (bandCfg?.color || "#06B6D4");
              const statusLabel = overrideToAction ? "Action Required" : bandCfg?.label;
              // Drive the explanation off the actual lab result. When BOTH
              // E. coli AND Coliforms are positive, mention both —
              // never claim "acceptable" or "no issues" if either is.
              const messageText = overrideToAction ? (() => {
                const e = !!reportFacts?.e_coli_detected;
                const c = !!reportFacts?.coliforms_detected;
                if (e && c) return "E. coli and Coliforms were detected in this water sample. The water does not meet the Australian Drinking Water Guidelines (ADWG) and needs to be addressed to return it to an acceptable and safe drinking level.";
                if (e)       return "E. coli was detected in this water sample. The water does not meet the Australian Drinking Water Guidelines (ADWG) and needs to be addressed to return it to an acceptable and safe drinking level.";
                if (c)       return "Coliforms were detected in this water sample. The water does not meet the Australian Drinking Water Guidelines (ADWG) and needs to be addressed to return it to an acceptable and safe drinking level.";
                return "Health-related parameters in this sample are outside the Australian Drinking Water Guidelines and should be addressed.";
              })() : (
                latest.band === "excellent" ? "Your water is performing very well. Keep up your annual retest cadence." :
                latest.band === "good" ? "Your water is in good shape with only minor watch-points to monitor." :
                latest.band === "fair" ? "Your water is acceptable but worth keeping an eye on." :
                latest.band === "attention" ? "Your water shows areas that should be addressed soon." :
                latest.band === "action" ? "Your water requires action — please review the recommendations." :
                ""
              );
              return (
                <div
                  data-testid="health-score-highlight"
                  className="mt-8 rounded-2xl border-2 border-slate-300 print:border print:border-slate-500 overflow-hidden bg-white shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* LEFT SCORE PANEL — compact, full height on desktop,
                        stacks above on mobile. Soft band-tinted background
                        with the accent stripe on the leading edge. */}
                    <div
                      data-testid="health-score-tile"
                      className="flex-shrink-0 px-7 py-6 sm:py-7 sm:w-[200px] flex flex-col items-center justify-center text-center"
                      style={{
                        background: `${accent}14`,
                        borderRight: "1px solid #cbd5e1",
                        borderLeft: `8px solid ${accent}`,
                      }}
                    >
                      <div className="flex items-baseline gap-1 leading-none">
                        <span className="font-display font-bold text-[58px] sm:text-[64px] tabular-nums" style={{ color: accent }}>{latest.health_score}</span>
                        <span className="font-display font-semibold text-lg sm:text-xl text-slate-500 tabular-nums">/100</span>
                      </div>
                      <div className="mt-3 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-600">Score</div>
                    </div>
                    {/* RIGHT CONTENT — eyebrow on its own line, status on
                        its own line as a bold uppercase badge, paragraph
                        with comfortable spacing below. */}
                    <div className="flex-1 min-w-0 px-6 sm:px-8 py-6 sm:py-7">
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>
                        Water Health Score™
                      </div>
                      <div className="mt-2">
                        <span
                          data-testid="band-label"
                          className="inline-flex items-center px-3 py-1 rounded-md text-[13px] sm:text-sm font-bold uppercase tracking-[0.12em] border-2"
                          style={{ background: "#fff", color: accent, borderColor: accent }}
                        >
                          {statusLabel}
                        </span>
                      </div>
                      <p data-testid="band-message" className="mt-4 text-slate-800 text-[15px] sm:text-base leading-relaxed">
                        {messageText}
                      </p>
                      <div className="mt-4 text-[12px] text-slate-500">
                        Based on {tests.length} test{tests.length === 1 ? "" : "s"} for this property. Last updated {formatDateAU(latest.collected_at)}.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {!latest && (
              <div className="mt-8 text-center text-slate-400 text-sm">No published tests yet — sample is being processed.</div>
            )}
          </div>

          <aside className="lg:col-span-4 bg-slate-900 text-white rounded-3xl p-7 lg:p-8 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-cyan-500/25 rounded-full blur-3xl" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold">Next retest</div>
              <div className="font-display text-2xl font-bold mt-2">
                {sched ? formatDateAU(sched.next_due_at) : "Not scheduled"}
              </div>
              <p className="text-sm text-slate-300 mt-2">We'll send you a reminder 30 days before — and a one-click rebook link.</p>
              <Link
                to={`/book?test=${sched?.test_id || "tank"}&property=${p.id}`}
                data-testid="passport-rebook-btn"
                className="btn-aqua group mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"
              >
                One-click rebook <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="mt-5 pt-5 border-t border-white/10 flex items-center gap-2 text-xs text-slate-400">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                Annual cadence · {sched?.cadence_months || 12} months
              </div>
            </div>
          </aside>
        </div>

        {/* Jun 2026 — Bore 360 decision-matrix result (bore tests only,
            published snapshot). Customer sees the final level cleanly —
            no override metadata is ever surfaced here. */}
        {data.bore360_assessment && (
          <div className="mt-8">
            <Bore360Panel assessment={data.bore360_assessment} dataTestId="passport-bore360-panel" />
          </div>
        )}

        {/* Property & Water System info */}
        <div className="mt-8">
          <PropertyInfoCard propertyId={id} canEdit />
        </div>

        {/* ADWG comparison table (SSoT-driven). The footer links straight to
            the full Official COA PDF download so customers can drill in
            without scrolling around the property page. */}
        {adwgRows.length > 0 && (
          <div className="mt-8">
            <AdwgComparisonTable
              rows={adwgRows}
              coaHref={`${process.env.REACT_APP_BACKEND_URL}/api/passport/property/${id}/report.pdf`}
              dataTestId="passport-adwg-table"
            />
          </div>
        )}

        {/* Jun 2026 — Langelier Saturation Index (calculated index; same
            backend SSoT as the COA PDF so both always match). */}
        {lsi && (
          <div className="mt-4">
            <LsiIndexCard lsi={lsi} history={lsiHistory} dataTestId="passport-lsi-card" />
          </div>
        )}

        {/* Pristine Water Systems CTA (only shown when recommendations exist + flag enabled) */}
        <div className="mt-8">
          <PristineQuoteCTA
            propertyId={id}
            hasRecommendations={(aiSummary?.recommended_actions?.length || 0) > 0 || adwgRows.some((r) => r.status !== "pass")}
            dataTestId="passport-pristine-cta"
          />
        </div>

        {/* Recommendations */}
        {aiSummary && (
          <section data-testid="passport-ai-summary" className="mt-8 rounded-3xl bg-gradient-to-br from-cyan-50 via-white to-white border border-cyan-200 p-7 lg:p-9">
            {aiSummary.requires_stephen_review && (
              <div data-testid="passport-stephen-review-banner" className="mb-5 rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-semibold mb-1">⚠ This requires review by Stephen before sending.</div>
                <div className="text-sm">The automated summary detected inconsistent or ambiguous data and has paused for manual review. Please open this test in <a className="underline" href="/admin/lab">/admin/lab</a> to investigate.</div>
              </div>
            )}
            <div className="flex items-center gap-2 mb-1">
              <Brain className="w-4 h-4 text-cyan-700" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">Your plain-English summary</div>
            </div>
            <h2 className="font-display text-2xl font-semibold text-slate-900 mt-1.5 mb-4">What your results mean</h2>
            {aiSummary.narrative && (
              <p data-testid="passport-summary-narrative" className="text-slate-700 leading-relaxed whitespace-pre-line text-[15px]">{aiSummary.narrative}</p>
            )}
            {aiSummary.key_findings?.length > 0 && (
              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Key findings</div>
                <ul className="space-y-1.5 text-[15px] text-slate-700">
                  {aiSummary.key_findings.map((f, i) => (
                    <li key={`kf-${i}-${(f || "").slice(0, 32)}`} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-2 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {aiSummary.recommended_actions?.length > 0 && (
              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Recommended actions</div>
                <ul className="space-y-1.5 text-[15px] text-slate-700">
                  {aiSummary.recommended_actions.map((a, i) => (
                    <li key={`ra-${i}-${(a || "").slice(0, 32)}`} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />{a}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
              This plain-English summary was reviewed and approved by our lab team. The deterministic recommendations panel below remains the source of truth.
            </p>
            {reportMeta?.available ? (
              <button
                onClick={downloadReport}
                data-testid="passport-download-report"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-cyan-600 text-white text-sm font-semibold transition-colors"
              >
                <FileDown className="w-4 h-4" /> Download full PDF report
              </button>
            ) : reportMeta?.reason === "report_under_review" ? (
              <p data-testid="passport-report-pending" className="mt-5 text-xs text-amber-700 italic">Your full PDF report is being reviewed by our lab team.</p>
            ) : null}
          </section>
        )}

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <section data-testid="passport-recommendations" className="mt-8 bg-white rounded-3xl border border-slate-200 p-7">
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-5">Plain-English recommendations</h2>
            <div className="space-y-3">
              {recommendations.map((r, i) => {
                const sty = {
                  action: { bg: "bg-rose-50", border: "border-rose-200", dot: "bg-rose-500", text: "text-rose-700" },
                  watch:  { bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500", text: "text-amber-700" },
                  ok:     { bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500", text: "text-emerald-700" },
                }[r.severity] || { bg: "bg-slate-50", border: "border-slate-200", dot: "bg-slate-400", text: "text-slate-700" };
                return (
                  <div key={r.id || r.key || `rec-${i}-${(r.title || "").slice(0, 32)}`} className={`rounded-2xl border ${sty.border} ${sty.bg} p-5`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full ${sty.dot} mt-2 flex-shrink-0`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-display font-semibold text-slate-900">{r.title}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${sty.text}`}>{r.severity}</span>
                        </div>
                        <p className="text-[15px] text-slate-700 leading-relaxed">{r.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-[12px] text-slate-500 leading-relaxed">
              Recommendations are general guidance based on the most recent test. For tailored treatment advice, request a free Pristine Water Systems proposal/assessment from your Summary Report or contact our lab team.
            </p>
            <CustomerActionButtons
              testId={latest?.id}
              recommendations={recommendations}
              customerDefaults={{ address: p?.address }}
            />
          </section>
        )}

        {/* Phase 2L-A132B — Per-test recommendations history.
            Reads ONLY the approved snapshot written at publish time
            (db.report_payloads.recommendations). No draft AI notes,
            no admin/internal comments, no new recommendation
            generation here. Tests without an approved payload are
            hidden from this section so we don't render an empty card. */}
        {tests.length > 1 && (
          <section
            data-testid="passport-test-history"
            className="mt-8 bg-white rounded-3xl border border-slate-200 p-7"
          >
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-1">
              AWARE Water Health Record test history
            </h2>
            <p className="text-sm text-slate-500 mb-5">
              Each published test below shows the approved recommendations from the report you received at the time.
            </p>
            <ol className="space-y-4">
              {tests.map((t) => (
                <li
                  key={t.id}
                  data-testid={`passport-history-item-${t.id}`}
                  className="rounded-2xl border border-slate-200 bg-slate-50/50 p-5"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400">
                        Collected
                      </div>
                      <div className="mt-0.5 font-display font-semibold text-slate-900">
                        {t.collected_at ? formatDateAU(t.collected_at) : "—"}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {t.test_name || "AWARE Water Health Record test"}
                        {t.booking_reference && (
                          <> · <span className="font-mono">{t.booking_reference}</span></>
                        )}
                      </div>
                    </div>
                    {(t.health_score !== undefined && t.health_score !== null) && (() => {
                      const cfg = BAND_CFG[t.band] || BAND_CFG.fair;
                      return (
                        <div
                          data-testid={`passport-history-score-${t.id}`}
                          className="text-right flex-shrink-0"
                        >
                          <div
                            className="font-display font-bold text-2xl tabular-nums"
                            style={{ color: cfg.color }}
                          >
                            {t.health_score}
                          </div>
                          <div
                            className="text-[10px] uppercase tracking-wider font-bold"
                            style={{ color: cfg.color }}
                          >
                            {cfg.label}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {Array.isArray(t.recommendations) && t.recommendations.length > 0 && (
                    <div
                      data-testid={`passport-history-recommendations-${t.id}`}
                      className="mt-4 pt-4 border-t border-slate-200"
                    >
                      <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-700 mb-2">
                        Recommended actions
                      </div>
                      <ul className="space-y-2">
                        {t.recommendations.map((r, i) => (
                          <li
                            key={r.id || `${t.id}-rec-${i}`}
                            className="text-sm text-slate-700 leading-relaxed"
                          >
                            <div className="font-semibold text-slate-900">{r.title}</div>
                            {r.body && (
                              <div className="mt-0.5 text-slate-600">{r.body}</div>
                            )}
                            {Array.isArray(r.suggested_actions) && r.suggested_actions.length > 0 && (
                              <ul className="mt-1 ml-4 list-disc space-y-0.5 text-slate-600">
                                {r.suggested_actions.map((a, j) => (
                                  <li key={j}>{a}</li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Trends */}
        {tests.length > 0 && (
          <section className="mt-8 bg-white rounded-3xl border border-slate-200 p-7">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-semibold text-slate-900">Trend graphs</h2>
              <span className="text-xs text-slate-500">Safe range shaded green</span>
            </div>
            {/* Phase 2L-A108 — Water Health Score™ over time.
                Read-only chart driven from existing health_score values
                on each published test. No new scoring logic. */}
            <div className="mb-7 pb-7 border-b border-slate-100">
              <ScoreTrendChart tests={tests} />
            </div>
            <div className="grid lg:grid-cols-2 gap-7">
              {PARAMS.map((param) => (
                <TrendChart key={param} tests={tests} parameter={param} />
              ))}
            </div>
          </section>
        )}

        {/* Timeline (merged: tests + AI summaries + transfers + membership + manual events) */}
        <section className="mt-8">
          <PropertyTimeline
            events={timelineEvents}
            title="Property water timeline"
            onOpenReport={openReportForEvent}
          />
        </section>
      </main>

      {showShare && <ShareModal propertyId={id} onClose={() => setShowShare(false)} />}
      {showTransfer && (
        <TransferModal
          propertyId={id}
          propertyAddress={p.address}
          onClose={() => setShowTransfer(false)}
          onSubmitted={() => { /* future: refresh transfers list */ }}
        />
      )}
    </div>
  );
}
